import { AfterViewInit, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { fadeIn, fadeOut } from 'src/app/animations';
import { ContactType } from 'src/app/models/enum/contact-type.enum';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { sortBy, orderBy } from 'lodash-es';
import { MyWorkJob } from 'src/app/models/my-work/my-work-job.model';
import * as moment from 'moment';
import { NavigationActionButton } from '../../navigation-bar/models/navigation-action-button.model';
import { JobLogbook } from 'src/app/models/my-work/job-logbook.model';
import { MyWorkService } from 'src/app/services/sections/job/my-work.service';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { Observable, of, Subject } from 'rxjs';
import { Frequency } from '@vitralogy/ng-digital-log';
import { AssignmentType } from 'src/app/models/enum/job-assignment-type.enum';
import { JobBriefInfo } from 'src/app/models/my-work/job-brief-info.model';
import { ViewCategory } from './models/view-category.enum';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserAccountInfo } from 'src/app/models/user-account-info.model';
import { AuthPage } from 'src/app/models/auth/auth-page.model';
import { DetailsRounded } from '@material-ui/icons';

@Component({
	templateUrl: './my-work.component.html',
	styleUrls: ['./my-work.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss', '../../stylesheets/job-contacts.scss'],
	animations: [fadeIn(300), fadeOut(300)]
})
export class MyWorkComponent implements OnInit, AfterViewInit, OnDestroy {

	public formModel: FormGroup;

	public self = this;
	public isVendor = false;
	public hasJobShifts = false;

	public jobs: MyWorkJob[] = [];
	public jobsLogbook: MyWorkJob[] = [];
	public jobsByDate: Map<string, { name: string, jobs: MyWorkJob[] }> = new Map<string, { name: string, jobs: MyWorkJob[] }>();
	public jobsByProject: Map<string, { name: string, jobs: MyWorkJob[] }> = new Map<string, { name: string, jobs: MyWorkJob[] }>();
	public jobsBySite: Map<number, { name: string, jobs: MyWorkJob[] }> = new Map<number, { name: string, jobs: MyWorkJob[] }>();
	public jobsByDayOfWeek: Map<string, { name: string, jobs: MyWorkJob[] }> = new Map<string, { name: string, jobs: MyWorkJob[] }>();

	public jobsInDigitalLogbooks: Map<string, { name: string, jobs: JobLogbook[] }> = new Map<string, { name: string, jobs: JobLogbook[] }>();

	public jobsInSmartDocs: Map<string, { name: string, jobs: MyWorkJob[] }> = new Map<string, { name: string, jobs: MyWorkJob[] }>();

	public jobsByDateKeys: string[] = [];
	public jobsByProjectNameKeys: string[] = [];
	public jobsBySiteNameKeys: string[] = [];
	public jobsByDayOfWeekNameKeys: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	public jobInDigitalLogbooksNameKeys: string[] = [];
	public jobsInSmartDocsKeys: string[] = [];

	public siteFilterModel: { id: number, name: string }[];
	public projectFilterModel: { id: number, name: string }[];
	public selectedViewIndex = 0;
	public shouldTryOneJobRedirect = false;

	public userAccountInfoModel: UserAccountInfo;
	public accountRouteUrls: Map<AuthPage, string>;

	public indexedViewNames = [
		{ key: 'scheduled', subtypes: ['project', 'site', 'day-of-week'] },
		{ key: 'logbook', subtypes: [] },
		{ key: 'documents', subtypes: [] },
	];

	public selectedCategoryIndex = ViewCategory.ScheduledWork;

	public backupFilters: any;
	public filtersFormModel: FormGroup;

	public isFilterApplied = false;
	public isDataLoading = false;

	public isShowAllShiftsChanged = false;

	public navigationBarActions: Map<string, NavigationActionButton>;

	public get isShowingAllShifts(): boolean {
		return !!this.filtersFormModel?.get('sas')?.value;
	}

	public readonly ButtonIcons = ButtonIcons;
	public readonly ContactType = ContactType;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public readonly moment = moment;
	public readonly AssignmentType = AssignmentType;
	public readonly ViewCategory = ViewCategory;

	private readonly SPLITTER = '[*##[';

	private jobsBriefInfo: JobBriefInfo[] = [];
	private cancelRequest$: Subject<void> = new Subject<void>();

	constructor(
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private myWorkService: MyWorkService,
		private jwt: JwtHelperService
	) {
		this.isVendor = this.activatedRoute.snapshot.data.isVendor;
		this.hasJobShifts = this.activatedRoute.snapshot.data.hasJobShifts;
		this.jobsBriefInfo = this.activatedRoute.snapshot.data.jobsBriefInfo;
		this.userAccountInfoModel = this.activatedRoute.snapshot.data.accountInfo as UserAccountInfo;
		this.accountRouteUrls = this.activatedRoute.snapshot.data.accountRouteUrls;

		this.filtersFormModel = new FormGroup({
			pid: new FormControl(),
			sid: new FormControl(),
			sq: new FormControl(),
			sas: new FormControl(false)
		});

		this.filtersFormModel.get('sas').valueChanges
			.subscribe(_ => {
				this.isShowAllShiftsChanged = true;
			});
	}

	ngOnInit(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = false;

		this.navigationBarActions = GlobalSettingsService.navigationBarActions;

		GlobalSettingsService.isJobCompletedStatusVisible = false;
		GlobalSettingsService.navigationBarActions = new Map<string, NavigationActionButton>();

		this.injectActionButtons();

		this.populateFilterFields();

		try {
			const queryParamMap = this.activatedRoute.snapshot.queryParamMap;

			if (queryParamMap.keys.length) {
				this.filtersFormModel.patchValue({
					pid: parseInt(queryParamMap.get('pid'), 10),
					sid: parseInt(queryParamMap.get('sid'), 10),
					sq: queryParamMap.get('sq'),
					sas: queryParamMap.get('sas')
				});

				const category = queryParamMap.get('cat')?.toLowerCase();
				const numberCategory = parseInt(category, 10);

				if (isNaN(numberCategory)) {
					this.selectedCategoryIndex = this.indexedViewNames.findIndex(item => item.key === category);
					if (this.selectedCategoryIndex < 0) { this.selectedCategoryIndex = ViewCategory.ScheduledWork; }
				} else {
					this.selectedCategoryIndex = numberCategory - 1;
					if (this.selectedCategoryIndex < 0 || this.selectedCategoryIndex >= this.indexedViewNames.length) { this.selectedCategoryIndex = 0; }
				}

				const view = queryParamMap.get('v')?.toLowerCase();
				const numberView = parseInt(view, 10);

				if (isNaN(numberView)) {
					this.selectedViewIndex = this.indexedViewNames[this.selectedCategoryIndex]?.subtypes.findIndex(value => value === view);
					if (this.selectedViewIndex < 0) { this.selectedViewIndex = 0; }
				} else {
					this.selectedViewIndex = numberView - 1;
					if (this.selectedViewIndex < 0 || this.selectedViewIndex > this.indexedViewNames[this.selectedCategoryIndex]?.subtypes.length) { this.selectedViewIndex = 0; }
				}

				this.applyFilters(true);

				this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: null });

				this.shouldTryOneJobRedirect = true;
			} else {
				// Filters Form object model
				const stringStorageFilters = sessionStorage.getItem('my-work-filters');
				let storageFilters = {} as any;

				if (!!stringStorageFilters) {
					storageFilters = JSON.parse(stringStorageFilters);
					this.filtersFormModel.patchValue(storageFilters);
				}

				if (storageFilters.selectedCategoryIndex) {
					this.selectedCategoryIndex = parseInt(storageFilters.selectedCategoryIndex, 10);
					if (isNaN(this.selectedCategoryIndex)) { this.selectedCategoryIndex = ViewCategory.ScheduledWork; }
				}

				if (storageFilters.selectedViewIndex) {
					this.selectedViewIndex = parseInt(storageFilters.selectedViewIndex, 10);
					if (isNaN(this.selectedViewIndex)) { this.selectedViewIndex = 0; }
				}

				this.applyFilters(true);
			}
		}
		catch { }

	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		this.selectedViewIndex = 0;
	}

	ngOnDestroy(): void {
		GlobalSettingsService.navigationBarActions = this.navigationBarActions;
	}

	public updateJobsSource(): Observable<MyWorkJob[] | JobLogbook[]> {
		let jobsSource$: Observable<MyWorkJob[] | JobLogbook[]> = null;
		const formData = this.filtersFormModel.getRawValue();

		this.cancelRequest$.next();

		this.jobs = [];

		switch (this.selectedCategoryIndex) {
			case ViewCategory.ScheduledWork: jobsSource$ = this.myWorkService.getMyWorkJobs(TokenHelper.getToken(this.activatedRoute), formData.sas || false); break;
			case ViewCategory.DigitalLogbook: jobsSource$ = this.myWorkService.getMyWorkJobLogbook(TokenHelper.getToken(this.activatedRoute)); break;
			case ViewCategory.SmartDocs: jobsSource$ = this.myWorkService.getMyWorkSmartDocs(TokenHelper.getToken(this.activatedRoute)); break;
			default: jobsSource$ = of(null); break;
		}

		return jobsSource$.pipe(
			takeUntil(this.cancelRequest$),
			map(result => {
				result?.forEach(item => item.assets = item.assetsConcatenation?.split(this.SPLITTER));
				return result;
			}),
			tap(result => {
				this.jobs = orderBy(result, [
					(item: MyWorkJob) => item.jobDate,
					(item: MyWorkJob) => item.jobName.toLowerCase()
				], ['desc', 'asc']);
			})
		);
	}

	public applyFilters(forceUpdateDataset: boolean = false): void {
		this.isDataLoading = true;

		const filterData = this.filtersFormModel.getRawValue();
		const shouldUpdateDataset = forceUpdateDataset || this.checkShouldUpdateDataset();
		const dataUpdates$ = shouldUpdateDataset ? this.updateJobsSource() : of(null);

		this.selectedCategoryIndex = this.selectedCategoryIndex || 0;
		filterData.selectedCategoryIndex = this.selectedCategoryIndex || 0;

		this.selectedViewIndex = this.selectedViewIndex || 0;
		filterData.selectedViewIndex = this.selectedViewIndex;

		this.backupFilters = filterData;

		sessionStorage.setItem('my-work-filters', JSON.stringify(filterData));

		dataUpdates$.subscribe(() => {

			let data: MyWorkJob[];

			switch (this.selectedCategoryIndex) {
				case ViewCategory.ScheduledWork:

					data = this.jobs.filter(item =>
						(!filterData.sq || (filterData.sq && this.checkCustomFilter(item, filterData.sq))) &&
						(!filterData.pid || item.serviceContractId === filterData.pid || item.projectName === filterData.pid) &&
						(!filterData.sid || item.siteId === filterData.sid)
					);

					this.jobsBySite.clear();
					this.jobsByProject.clear();
					this.jobsByDate.clear();
					this.jobsByDayOfWeek.clear();

					const dates: Date[] = [];

					data.forEach(item => {
						if (!this.jobsBySite.has(item.siteId)) {
							this.jobsBySite.set(item.siteId, { name: item.siteName, jobs: [item] });
						} else {
							this.jobsBySite.get(item.siteId).jobs.push(item);
						}

						if (!this.jobsByProject.has(item.projectName)) {
							this.jobsByProject.set(item.projectName, { name: item.projectName, jobs: [item] });
						} else {
							this.jobsByProject.get(item.projectName).jobs.push(item);
						}

						const date = moment.parseZone(item.jobDate).format('MM/DD/YYYY');
						if (!this.jobsByDate.has(date)) {
							dates.push(item.jobDate);
							this.jobsByDate.set(date, { name: date, jobs: [item] });
						} else {
							this.jobsByDate.get(date).jobs.push(item);
						}

						const dayName = moment(item.jobDate).format('dddd');
						if (!this.jobsByDayOfWeek.has(dayName)) {
							this.jobsByDayOfWeek.set(dayName, { name: dayName, jobs: [item] });
						} else {
							this.jobsByDayOfWeek.get(dayName).jobs.push(item);
						}
					});

					this.jobsByDateKeys = dates.sort().map(item => moment.parseZone(item).format('MM/DD/YYYY'));

					this.jobsBySiteNameKeys = sortBy(Array.from(this.jobsBySite.keys()), [
						(item: number) => this.jobsBySite.get(item).name.toLowerCase()
					]);

					this.jobsByProjectNameKeys = sortBy(Array.from(this.jobsByProject.keys()), [
						(item: string) => this.jobsByProject.get(item).name.toLowerCase()
					]);
					break;

				case ViewCategory.DigitalLogbook:
					data = this.jobs.filter(item =>
						(!filterData.sq || (filterData.sq && this.checkCustomFilter(item, filterData.sq))) &&
						(!filterData.pid || item.serviceContractId === filterData.pid || item.projectName === filterData.pid) &&
						(!filterData.sid || item.siteId === filterData.sid)
					);

					this.jobsInDigitalLogbooks.clear();

					data.forEach((item: JobLogbook) => {
						item.details.sort((a, b) => {
							return new Date(a?.lastSavedDate).getTime() < new Date(b?.lastSavedDate).getTime() ? 1 : -1;
						});

						if (!this.jobsInDigitalLogbooks.has(item.jobName)) {
							this.jobsInDigitalLogbooks.set(item.jobName, { name: item.jobName, jobs: [{ ...item, lastSavedDate: item.details[0]?.lastSavedDate}] });
						} else {
							this.jobsInDigitalLogbooks.get(item.jobName).jobs.push({ ...item, lastSavedDate: item.details[0]?.lastSavedDate});
						}
					});

					this.jobInDigitalLogbooksNameKeys = sortBy(Array.from(this.jobsInDigitalLogbooks.keys()), [
						(item: string) => this.jobsInDigitalLogbooks.get(item).name.toLowerCase()
					]);

					Array.from(this.jobsInDigitalLogbooks.values())
						.forEach(collection => {
							collection.jobs = orderBy(collection.jobs, [
								(item: JobLogbook) => item.lastSavedDate ? moment(item.lastSavedDate).format('YYYY-MM-DD') : '0000-00-00'
							], ['desc']);

							this.jobsInDigitalLogbooks.set(collection.jobs[0]?.jobName, collection);
						});
					break;

				case ViewCategory.SmartDocs:

					data = this.jobs.filter(item =>
						(!filterData.sq || (filterData.sq && this.checkCustomFilter(item, filterData.sq))) &&
						(!filterData.pid || item.serviceContractId === filterData.pid || item.projectName === filterData.pid) &&
						(!filterData.sid || item.siteId === filterData.sid)
					);

					this.jobsInSmartDocs.clear();

					data.forEach((item: MyWorkJob) => {

						if (!this.jobsInSmartDocs.has(item.projectName)) {
							this.jobsInSmartDocs.set(item.projectName, { name: item.projectName, jobs: [item] });
						} else {
							this.jobsInSmartDocs.get(item.projectName).jobs.push(item);
						}
					});

					this.jobsInSmartDocsKeys = sortBy(Array.from(this.jobsInSmartDocs.keys()), [
						(item: string) => this.jobsInSmartDocs.get(item).name.toLowerCase()
					]);

					break;

				default: break;
			}

			this.isFilterApplied = !!(filterData.pid || filterData.sid || filterData.sq);

			if (this.shouldTryOneJobRedirect) {
				this.redirectOnSingleJob();
				this.shouldTryOneJobRedirect = false;
			}

			this.isDataLoading = false;
		});
	}

	public checkCustomFilter(job: MyWorkJob, searchQuery: string): boolean {
		searchQuery = searchQuery.toLowerCase();

		return job.jobName.toLowerCase().includes(searchQuery) ||
			(job.siteName && job.siteName.toLowerCase().includes(searchQuery)) ||
			(job.asset && job.asset.toLowerCase().includes(searchQuery)) ||
			(job.projectName && job.projectName.toLowerCase().includes(searchQuery)) ||
			(job.assets && !!job.assets.find(asset => asset.toLowerCase().includes(searchQuery)));
	}

	public onHide(): void {
		if (this.filtersFormModel) {
			this.filtersFormModel.patchValue(this.backupFilters);
		}

		this.isFilterApplied = !!(this.backupFilters?.pid || this.backupFilters?.sid || this.backupFilters?.sq);
	}

	public assignmentTypeLabel(assignmentType: AssignmentType): string {
		let type = 'Unassigned';
		switch (assignmentType) {
			case AssignmentType.AssignedToYou:
				type = 'Assigned to You';
				break;
			case AssignmentType.NotAssignedToYou:
				type = 'NOT Assigned to You';
				break;
			default:
				break;
		}

		return type;
	}

	public saveTabChange(index: number): void {
		const filterData = this.filtersFormModel.getRawValue();
		filterData.selectedViewIndex = index;
		this.selectedViewIndex = index;
		sessionStorage.setItem('my-work-filters', JSON.stringify(filterData));
	}

	public saveCategoryChange(index: number): void {
		const filterData = this.filtersFormModel.getRawValue();

		filterData.selectedCategoryIndex = index;
		this.selectedCategoryIndex = index;

		filterData.selectedViewIndex = 0;
		this.selectedViewIndex = 0;

		sessionStorage.setItem('my-work-filters', JSON.stringify(filterData));

		this.applyFilters(true);
	}

	public getFrequencyNaming(frequency: Frequency): string {
		switch (frequency) {
			case null: return 'One Time';
			case Frequency.OnceDaily: return 'Daily';
			case Frequency.SpecificDays: return 'Specific days of the week';
			case Frequency.OnceWeekly: return 'Weekly';
			case Frequency.OnceMonthly: return 'Monthly';
			case Frequency.TwiceMonthly: return 'Twice every month';
			case Frequency.OnceQuarterly: return 'Quarterly';
			case Frequency.TwiceAnnually: return 'Twice every year';
			case Frequency.OnceAnnually: return 'Yearly';
			default: return null;
		}
	}

	public collectionHasValues(collection: Map<any, { name: string, jobs: any[] }>): boolean {
		const keys = Array.from(collection.keys());
		const values = Array.from(collection.values());

		if (!keys?.length || !values?.length) {
			return false;
		}

		if (!!values.find(item => item.jobs?.length)) {
			return true;
		}

		return false;
	}

	public toggleShowAllShifts(): void {
		const currentValue = !!this.filtersFormModel.get('sas').value;
		this.filtersFormModel.get('sas').setValue(!currentValue);
		this.applyFilters();
	}

	private injectActionButtons(): void {
		const userData = this.jwt.decodeToken();

		GlobalSettingsService.navigationBarActions.set(
			'mywork_switch_account',
			{
				action: () => this.router.navigate(
					['/login'],
					{ queryParams: { email: userData?.email || '', useMobile: true } }
				),
				text: 'Switch Account',
				isInDropdownMenu: false,
				isTextOnlyShownInDropdown: true,
				icon: 'sync_alt',
				listOrder: 0
			});

		GlobalSettingsService.navigationBarActions.set(
			'my-work-access-button',
			{
				icon: 'list_alt',
				listOrder: 1,
				text: 'My Work',
				isInDropdownMenu: false,
				isTextOnlyShownInDropdown: true,
				action: () => this.router.navigate(['/', ...this.accountRouteUrls.get(AuthPage.MyWork).split('/')])
			}
		);

		if (!this.userAccountInfoModel.isVendorUser || (this.userAccountInfoModel.isVendorUser && this.userAccountInfoModel.isSelfPerform)) {
			GlobalSettingsService.navigationBarActions.set(
				'deficiencies-access-button',
				{
					icon: 'deficiencies',
					isCustomSvgIcon: true,
					text: 'Deficiencies',
					isInDropdownMenu: false,
					isTextOnlyShownInDropdown: true,
					listOrder: 2,
					action: () => this.router.navigate(['/', ...this.accountRouteUrls.get(AuthPage.Deficiencies).split('/')])
				}
			);
		}

	}

	private checkShouldUpdateDataset(): boolean {
		if (this.isShowAllShiftsChanged) {
			this.isShowAllShiftsChanged = false;
			return true;
		}

		return false;
	}

	private populateFilterFields(): void {

		const siteDictionary = new Map<number, string>();
		const projectDictionary = new Map<string, string>();

		if (!!this.jobsBriefInfo) {
			this.jobsBriefInfo.forEach(item => {
				if (!siteDictionary.has(item.siteId)) { siteDictionary.set(item.siteId, item.siteName); }
				if (!projectDictionary.has(item.projectName)) { projectDictionary.set(item.projectName, item.projectName); }
			});
		}

		this.siteFilterModel = sortBy(
			Array.from(siteDictionary.keys()).map(key => ({ id: key, name: siteDictionary.get(key) })),
			[(item: { id: number, name: string }) => item.name]);

		this.projectFilterModel = sortBy(
			Array.from(projectDictionary.keys()).map(key => ({ id: key, name: projectDictionary.get(key) })),
			[(item: { id: string, name: string }) => item.name]);
	}

	private redirectOnSingleJob(): void {
		let values;

		if (this.selectedCategoryIndex === ViewCategory.DigitalLogbook) {
			values = Array.from(this.jobsInDigitalLogbooks.values());
		} else if (this.selectedCategoryIndex === ViewCategory.SmartDocs) {
			values = Array.from(this.jobsInSmartDocs.values());
		} else {
			return;
		}

		const jobIds: Set<number> = new Set<number>(values
			.map(item => item.jobs)
			.reduce((prev, current) => [...current, ...prev])
			.map(item => item.jobId)
		);

		if (jobIds && jobIds.size === 1) {
			window.location.replace(values[0].jobs[0].urlHash);
		}
	}
}
